import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DEFAULT_LOCATION, LocationPicker, Location } from '../components/LocationPicker';
import { PreferencesPanel } from '../components/PreferencesPanel';
import { SubscribeButtons } from '../components/SubscribeButtons';
import { FUNCTIONS_URL, supabase } from '../lib/supabase';
import { authCallbackUrl } from '../lib/urls';
import { defaultSettings, Settings } from '../lib/settings';
import { savePendingPurchase } from '../lib/pending-purchase';
import { useStore } from '../store';

export function Landing() {
  const { session, loading } = useStore();
  const [location, setLocation] = useState<Location>(DEFAULT_LOCATION);
  const [settings, setSettings] = useState<Settings>(defaultSettings());
  const [showPrefs, setShowPrefs] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && session) {
    return <Navigate to="/manage" replace />;
  }

  const trialIcsParams = new URLSearchParams({
    lat: String(location.lat),
    lng: String(location.lng),
    show_sunrise: String(settings.show_sunrise),
    show_sunset: String(settings.show_sunset),
    use_emoji: String(settings.use_emoji),
    event_duration_minutes: String(settings.event_duration_minutes),
  });
  const trialIcsUrl = `${FUNCTIONS_URL}/trial-ics?${trialIcsParams.toString()}`;

  const buy = () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setError('Enter a valid email.');
    }
    setError(null);
    const storeUrl = import.meta.env.VITE_LS_STORE_URL;
    if (!storeUrl) return setError('Checkout URL not configured.');

    // Nonce travels via localStorage — LS doesn't preserve arbitrary URL
    // params on the success redirect, so we can't round-trip through the URL.
    const nonce = crypto.randomUUID();
    savePendingPurchase(email, nonce);

    const params = new URLSearchParams({
      'checkout[email]': email,
      'checkout[custom][lat]': String(location.lat),
      'checkout[custom][lng]': String(location.lng),
      'checkout[custom][nonce]': nonce,
    });
    window.location.href = `${storeUrl}?${params.toString()}`;
  };

  const signIn = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setError('Enter a valid email.');
    }
    setError(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authCallbackUrl(), shouldCreateUser: false },
      });
      if (error) throw error;
      setStatus('Check your email for a sign-in link.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1>Sunrise and sunset, in your calendar.</h1>

      <LocationPicker value={location} onChange={setLocation} />

      <h2>
        Add this week to your calendar <span className="heading-meta">(free preview)</span>
      </h2>
      <SubscribeButtons icsUrl={trialIcsUrl} />

      <h2>
        Get auto-updates <span className="heading-meta">($2 for 3 years)</span>
      </h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Your calendar auto-updates. Change your location and preferences anytime.
      </p>
      <input
        id="email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        aria-label="Email"
        autoComplete="email"
        style={{ marginTop: '0.75rem' }}
      />
      <div className="buy-row">
        <button className="primary" onClick={buy} disabled={busy}>
          Pay $2
        </button>
        <button className="secondary" onClick={signIn} disabled={busy}>
          Sign in
        </button>
      </div>

      <div className="ghost-row">
        <button
          type="button"
          className="ghost"
          aria-pressed={showPrefs}
          aria-expanded={showPrefs}
          onClick={() => setShowPrefs((v) => !v)}
        >
          {showPrefs ? 'Hide preferences' : 'Preferences'}
        </button>
        <button
          type="button"
          className="ghost"
          aria-pressed={showDetails}
          aria-expanded={showDetails}
          onClick={() => setShowDetails((v) => !v)}
        >
          {showDetails ? 'Hide details' : 'Details'}
        </button>
      </div>

      {showPrefs && (
        <>
          <p className="soft" style={{ marginTop: '0.5rem' }}>
            More options coming soon — vote on what you want next.
          </p>
          <PreferencesPanel
            value={settings}
            onChange={(patch) => setSettings({ ...settings, ...patch })}
          />
        </>
      )}

      {showDetails && (
        <div className="details-body">
          <h3>What is this?</h3>
          <p>
            A calendar feed that adds sunrise and sunset times to your existing calendar app —
            Google Calendar, Apple Calendar, Outlook, or anything that subscribes to a URL. Your
            location decides the times.
          </p>

          <h3>How it works</h3>
          <p>
            Pick your location on the map. Click Google, Apple, or Outlook. Your calendar app
            subscribes to a feed at a stable URL. The feed rewrites daily for your location, so
            the next day&#39;s sunrise is always there without you doing anything.
          </p>

          <h3>Free vs paid</h3>
          <p>
            The free preview shows a rolling 7 days. Pay $2 and the feed covers 30 days ahead,
            always, for 3 years. There&#39;s nothing to install and nothing to cancel —
            when the 3 years are up, pay $2 again to keep it going.
          </p>

          <h3>Why would I want this?</h3>
          <p>
            If you run, cycle, photograph, farm, fish, stargaze, or care about being outside in
            good light — seeing sunrise and sunset next to your meetings makes you plan around
            daylight instead of against it.
          </p>

          <h3>Accuracy and timezones</h3>
          <p>
            Times come from standard astronomical formulas using your exact coordinates. Events
            appear in your local timezone, derived from your location — no configuration.
          </p>

          <h3>After you pay</h3>
          <p>
            Your browser returns here and you&#39;re signed in automatically. Your feed URL is ready
            in about a minute. You can change your location or preferences anytime from the
            sign-in page.
          </p>

          <h3>Is this a subscription?</h3>
          <p>
            No. $2 covers 3 years of updates, and nothing auto-renews. When the 3 years are
            up, you can pay $2 again to continue — nothing recurring or automatic.
          </p>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {status && <p className="notice">{status}</p>}
    </>
  );
}
